<template>
  <div class="arrival-time-selector py-4 flex flex-col gap-4">
    <div
      v-for="(group, i) in filteredArrivalTimes"
      :id="`group_${i}`"
      :key="group.time_range_name"
      v-intersection-observer="onIntersect"
      class="scroll-mt-16"
      :class="groupClassName"
    >
      <span class="text-sm text-gray-80">{{ group.time_range_name }}</span>
      <div class="grid grid-rows-auto grid-cols-3 gap-2 mt-2">
        <template v-for="item in group.selltimes" :key="item.selltime">
          <div class="flex flex-col items-center gap-1">
            <nd-button
              size="small"
              type="secondary"
              class="w-full"
              :class="{ active: selectedDateTimeItem?.selltime === item.selltime }"
              @click="handleSellTimeClick(item)"
            >
              {{ timeStringFormatter(item.selltime) }}
            </nd-button>
            <span class="text-sm text-gray-80">{{ getItemPrice(item) }}</span>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { format, parseISO } from 'date-fns'

import { vIntersectionObserver } from '@vueuse/components'

import type { UiProductSelectedData } from '@/types/product'
import type { SselldateSelldate, SselldateSelltime } from '@/types/api'

const ALL_NON_NUMERIC_CHARACTERS = /[^\d.-]+/g

const { locale } = useI18n()

const groupClassName = 'arrival-time-group'

const selectedDateTimeItem = defineModel<SselldateSelltime>()
const activatedGroupIndex = defineModel<number>('activatedGroupIndex')

const props = withDefaults(
  defineProps<{
    selectedDate: [string, string]
    arrivalTimes: SselldateSelldate[]
    isCrossDay?: boolean
    crossDayItems?: UiProductSelectedData[]
    checkOutTime?: string
  }>(),
  {
    isCrossDay: false,
    crossDayItems: () => [],
    checkOutTime: '00:00:00',
  }
)

const emit = defineEmits<{
  (event: 'cross-day-sell-time-selected', value: UiProductSelectedData[]): void
}>()

const isSameDate = computed(() => {
  const [startDate, endDate] = props.selectedDate
  return startDate === endDate
})

const filteredArrivalTimes = computed(() => {
  if (!props.arrivalTimes) return []
  if (!isSameDate.value) return props.arrivalTimes

  const [startDate] = props.selectedDate
  const checkOutDateObj = parseISO(`${startDate} ${props.checkOutTime}`)

  return props.arrivalTimes.reduce((acc, cur) => {
    const tmpSellTimes =
      cur.selltimes?.filter(item => {
        if (!item.selltime) return false
        return parseISO(item.selltime) < checkOutDateObj
      }) || []
    if (tmpSellTimes.length) {
      acc.push({ ...cur, selltimes: tmpSellTimes })
    }
    return acc
  }, [] as SselldateSelldate[])
})

const tmpCrossDaySellTimeItems = computed(() => {
  if (isSameDate.value) return props.crossDayItems
  if (selectedDateTimeItem.value) return getCrossDayItemBySellTime(selectedDateTimeItem.value)
  return []
})

const handleSellTimeClick = (item: SselldateSelltime) => {
  if (!item.selltime) return ''
  selectedDateTimeItem.value = item
  if (props.isCrossDay) {
    nextTick(() => emit('cross-day-sell-time-selected', tmpCrossDaySellTimeItems.value))
  }
}

const getItemPrice = (item: SselldateSelltime) => {
  if (!item.selltime || !item.price) return ''

  if (props.isCrossDay && !isSameDate.value) {
    const filteredCrossDayItems = getCrossDayItemBySellTime(item)
    const totalPrice = filteredCrossDayItems.reduce((acc, cur) => acc + cur.price, 0)
    return priceFormat(totalPrice, locale.value)
  } else {
    return priceFormat(item.price, locale.value)
  }
}

const onIntersect = (entries: IntersectionObserverEntry[]) => {
  entries.forEach(entry => {
    if (!entry.isIntersecting) return
    activatedGroupIndex.value = Number(entry.target.id.replace(ALL_NON_NUMERIC_CHARACTERS, '')) || 0
  })
}

const getCrossDayItemBySellTime = (item: SselldateSelltime) => {
  if (!item.selltime || !item.price) return []

  // YYYY-MM-DD, HH:mm
  const [itemDate, itemTime] = item.selltime.split(' ')
  let crossDayItemIdx = props.crossDayItems.findLastIndex(item => item.dateTime?.split(' ')[0] === itemDate)
  if (itemTime && isEarlierInHourMinute(itemTime, props.checkOutTime)) {
    crossDayItemIdx = Math.max(crossDayItemIdx - 1, 0)
  }
  const result = props.crossDayItems.slice(crossDayItemIdx)
  result[0] = { dateTime: item.selltime, price: item.price, originPrice: item.origin_price || 0 }
  return result
}

const timeStringFormatter = (datetime?: string) => {
  if (!datetime) return ''

  return format(parseISO(datetime), 'HH:mm')
}

watch(
  () => props.selectedDate,
  newItem => {
    if (newItem[0] && newItem[1] && props.isCrossDay) {
      emit('cross-day-sell-time-selected', tmpCrossDaySellTimeItems.value)
    }
  },
  { immediate: true }
)
</script>

<style lang="scss" scoped></style>
